'use client';

import { useState } from 'react';
import Icon from './Icon';
import { Button } from '~/components/ui/button';
import { Calendar } from './Calendar';
import { parseAsString, useQueryState } from 'nuqs';
import type { DateRange } from 'react-day-picker';
import { getYear, startOfQuarter, endOfQuarter, format } from 'date-fns';

type DateRangePickerWithPresetsProps = {
    /** Default month for the calendar */
    defaultMonth?: Date;
    /** Callback when a date range is selected */
    onRangeChange?: (range: DateRange) => void;
    /** URL parameter names for from/to dates (if not provided, no URL state is used) */
    fromParam?: string;
    toParam?: string;
    /** Default values for the date range */
    defaultFrom?: string;
    defaultTo?: string;
};

export const DateRangePickerWithPresets: React.FC<DateRangePickerWithPresetsProps> = ({
    defaultMonth,
    onRangeChange,
    fromParam,
    toParam,
    defaultFrom,
    defaultTo,
}) => {
    const [presetYear, setPresetYear] = useState(() => getYear(new Date()));

    // URL state management - conditionally enabled based on params
    const [fromValue, setFromValue] = useQueryState(
        fromParam || '__disabled_from',
        parseAsString.withOptions({ history: 'replace', shallow: true }),
    );
    const [toValue, setToValue] = useQueryState(
        toParam || '__disabled_to',
        parseAsString.withOptions({ history: 'replace', shallow: true }),
    );

    // When URL state is enabled, use it directly. When disabled, use local state.
    const isUrlStateEnabled = !!(fromParam && toParam);

    const urlRange = {
        from: fromValue || defaultFrom,
        to: toValue || defaultTo,
    };
    const [localRange, setLocalRange] = useState<DateRange>(() =>
        toDateRange(isUrlStateEnabled ? urlRange : { from: defaultFrom, to: defaultTo }),
    );

    // Re-sync with the URL whenever it changes; a half-picked calendar range lives only locally until then.
    const urlRangeKey = isUrlStateEnabled ? `${urlRange.from}|${urlRange.to}` : null;
    const [syncedUrlRangeKey, setSyncedUrlRangeKey] = useState(urlRangeKey);
    if (urlRangeKey !== syncedUrlRangeKey) {
        setSyncedUrlRangeKey(urlRangeKey);
        if (urlRangeKey !== null) setLocalRange(toDateRange(urlRange));
    }

    const handleQuarterClick = (quarter: 1 | 2 | 3 | 4) => {
        const quarterDate = new Date(presetYear, (quarter - 1) * 3, 1);
        const range: DateRange = {
            from: startOfQuarter(quarterDate),
            to: endOfQuarter(quarterDate),
        };

        setLocalRange(range);

        if (isUrlStateEnabled) {
            setFromValue(formatDateStr(range.from!));
            setToValue(formatDateStr(range.to!));
        }

        onRangeChange?.(range);
    };

    const handleCalendarSelect = (range: DateRange | undefined) => {
        setLocalRange(range || { from: undefined, to: undefined });

        // When URL state is enabled, immediately apply calendar selections
        if (isUrlStateEnabled && range?.from && range?.to) {
            setFromValue(formatDateStr(range.from));
            setToValue(formatDateStr(range.to));
            onRangeChange?.(range);
        }
    };

    const handleApply = () => {
        if (localRange.from && localRange.to) {
            onRangeChange?.(localRange);
        }
    };

    const canApply = localRange.from && localRange.to;

    return (
        <div className="flex gap-4">
            <div className="flex flex-col gap-2 border-r pr-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={() => setPresetYear(y => y - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                    >
                        <Icon name="chevron-left" className="size-4" />
                    </button>
                    <span className="min-w-12 text-center text-sm font-medium">{presetYear}</span>
                    <button
                        type="button"
                        onClick={() => setPresetYear(y => y + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                    >
                        <Icon name="chevron-right" className="size-4" />
                    </button>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(1)}
                    className="justify-start"
                >
                    Q1 ({presetYear})
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(2)}
                    className="justify-start"
                >
                    Q2 ({presetYear})
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(3)}
                    className="justify-start"
                >
                    Q3 ({presetYear})
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuarterClick(4)}
                    className="justify-start"
                >
                    Q4 ({presetYear})
                </Button>
            </div>
            <div className="flex flex-col gap-3">
                <Calendar
                    mode="range"
                    selected={localRange}
                    onSelect={handleCalendarSelect}
                    captionLayout="dropdown"
                    numberOfMonths={2}
                    defaultMonth={defaultMonth}
                />
                {!isUrlStateEnabled && (
                    <div className="flex justify-end border-t pt-3">
                        <Button type="button" size="sm" onClick={handleApply} disabled={!canApply}>
                            Apply
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

function toDateRange(range: { from?: string | null; to?: string | null }): DateRange {
    return {
        from: range.from ? new Date(range.from) : undefined,
        to: range.to ? new Date(range.to) : undefined,
    };
}

function formatDateStr(d: Date): string {
    return format(d, 'yyyy-MM-dd');
}

// Export the component with URL state wrapper for convenience
export const DateRangePickerWithUrlState: React.FC<DateRangePickerWithPresetsProps> = props => (
    <DateRangePickerWithPresets {...props} />
);
